import { Component, EventEmitter, Input, OnDestroy, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { IAccessDetail, IUserProfile } from 'src/app/user-management/user-management-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { profileEmail, ProfileManagementService, profilePopupErrorKey } from '../profile-management.service';

@Component({
  selector: 'app-profile-mobile-confirm-access',
  templateUrl: './profile-mobile-confirm-access.component.html',
  styleUrls: ['./profile-mobile-confirm-access.component.scss'],
})
export class ProfileMobileConfirmAccessComponent implements OnDestroy {
  @Input() profileDetails!: IUserProfile;
  @Input() siteId!: string;
  @Input() timeZone = '';
  @Input() dialogRef?: { close: () => void };
  @Output() backRequested = new EventEmitter<void>();
  @Output() inviteSent = new EventEmitter<void>();
  isSubmitting = false;
  errorKey = '';
  expanded = new Set<string>();
  private destroyed = new Subject<void>();

  constructor(
    private service: ProfileManagementService,
    private translate: TranslateService
  ) {}

  get name(): string {
    return [this.profileDetails?.firstName, this.profileDetails?.middleName, this.profileDetails?.lastName]
      .filter(Boolean).join(' ');
  }

  get email(): string {
    return profileEmail(this.profileDetails);
  }

  get formattedPhone(): string {
    const raw = String(this.profileDetails?.phoneNumber || '').trim();
    const digits = raw.replace(/\D/g, '');
    const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    return national.length === 10
      ? `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`
      : raw;
  }

  get accesses(): IAccessDetail[] {
    const access = this.profileDetails?.userAccess;
    const all = [
      ...(access?.homeAccess || []),
      ...(access?.otherAccess || []),
      ...(access?.temporaryAccess || []),
    ];
    const unique = new Map<string, IAccessDetail>();
    all.forEach((item, index) => {
      const key = item.unitId || `${item.buildingName}|${item.floorName}|${item.unitName || item.entityName}|${index}`;
      if (!unique.has(key)) unique.set(key, item);
    });
    return Array.from(unique.values());
  }

  get canInvite(): boolean {
    return !!this.siteId && !!this.profileDetails?.email && !!this.profileDetails?.phoneNumber && !this.isSubmitting;
  }

  get timeSummary(): string {
    const access = this.accesses[0] as (IAccessDetail & { startTime?: string }) | undefined;
    if (!access) return '';
    if (access.repeatStartTime || access.repeatEndTime) {
      return `${access.repeatStartTime || '12:00 AM'} - ${access.repeatEndTime || '11:59 PM'}`;
    }
    const start = access.startTime ? this.formatDate(access.startTime) : this.translate.instant('ACCESS_TIME_NOW');
    const end = access.expireTime ? this.formatDate(access.expireTime) : this.translate.instant('NEVER');
    return `${start} - ${end}`;
  }

  get daysSummary(): string {
    const days = this.accesses[0]?.repeatDays;
    if (!days?.length) return '';
    const order = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dateFormat = new Intl.DateTimeFormat(this.translate.currentLang || undefined, { weekday: 'short', timeZone: 'UTC' });
    return order
      .map((day, index) => ({ day, index }))
      .filter(item => days.includes(item.day))
      .map(item => dateFormat.format(new Date(Date.UTC(2026, 0, 4 + item.index))))
      .join(', ');
  }

  toggleAccess(access: IAccessDetail): void {
    const key = this.accessKey(access);
    if (this.expanded.has(key)) this.expanded.delete(key);
    else this.expanded.add(key);
  }

  isExpanded(access: IAccessDetail): boolean {
    return this.expanded.has(this.accessKey(access));
  }

  viewAll(): void {
    this.expanded = new Set(this.accesses.map(access => this.accessKey(access)));
  }

  sendInvite(): void {
    if (!this.canInvite) return;
    this.errorKey = '';
    this.isSubmitting = true;
    this.service.mobileAccessInvite(this.siteId, this.profileDetails.email!, this.profileDetails.phoneNumber!)
      .pipe(takeUntil(this.destroyed), finalize(() => this.isSubmitting = false))
      .subscribe({
        next: result => {
          this.profileDetails.email = result.email;
          this.profileDetails.stagedEmail = result.stagedEmail ?? null;
          if (result.phoneNumber != null) this.profileDetails.phoneNumber = result.phoneNumber;
          this.profileDetails.invitationPending = true;
          this.inviteSent.emit();
        },
        error: error => {
          this.errorKey = profilePopupErrorKey(error, 'PROFILE_POPUP_INVITE_FAILED');
        },
      });
  }

  close(): void {
    if (!this.isSubmitting) this.dialogRef?.close();
  }

  private accessKey(access: IAccessDetail): string {
    return access.unitId || `${access.buildingName}|${access.floorName}|${access.unitName || access.entityName}`;
  }

  private formatDate(value: string): string {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString(this.translate.currentLang || undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
      timeZone: this.timeZone || undefined,
    });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }
}
